import { ReactNode } from 'react'
import { useUrlParams } from '../../utils/urlParams'
import useTranslation from '../../hooks/useTranslation'

interface QRParamsGuardProps {
  children: ReactNode
  showWarning?: boolean
}

/**
 * Component to guard against missing QR code parameters
 * Shows a warning if required parameters are missing
 */
export default function QRParamsGuard({ children, showWarning = true }: QRParamsGuardProps) {
  const { hasRequiredParams, getAllParams } = useUrlParams()
  const { t } = useTranslation()

  // If we have required parameters, render children normally
  if (hasRequiredParams()) {
    return <>{children}</>
  }

  // If showWarning is false, still render children but log the issue
  if (!showWarning) {
    return <>{children}</>
  }

  // Show warning message for missing parameters
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="text-6xl mb-4">📱</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {t('qr.invalidAccess') || 'Invalid Access'}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('qr.scanQRCode') || 'Please scan the QR code from your table to access the menu.'}
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            {t('qr.missingParameters') || 'Required parameters are missing from the URL.'}
          </p>
          <p className="text-xs text-yellow-600 mt-2">
            Current URL: {window.location.href}
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors"
        >
          {t('common.refresh') || 'Refresh Page'}
        </button>
      </div>
    </div>
  )
}